import type { Role, Session } from "./types";

const KEY = "mva.session";

function parseSession(raw: string | null): Session | null {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Session;
    return s && typeof s.token === "string" && (s.role === "student" || s.role === "employee") && s.name
      ? s
      : null;
  } catch {
    return null;
  }
}

export function readSession(): Session | null {
  if (typeof window === "undefined") return null;
  try {
    return parseSession(window.localStorage.getItem(KEY)) ?? parseSession(window.sessionStorage.getItem(KEY));
  } catch {
    return null;
  }
}

/** remember=true keeps the session across browser restarts; false ends it when the browser closes. */
export function writeSession(session: Session, remember = true) {
  const json = JSON.stringify(session);
  window.localStorage.removeItem(KEY);
  window.sessionStorage.removeItem(KEY);
  (remember ? window.localStorage : window.sessionStorage).setItem(KEY, json);
}

/** Replaces the stored session (e.g. after /auth/profile refreshes permissions) in whichever storage holds it. */
export function refreshStoredSession(session: Session) {
  const inLocal = window.localStorage.getItem(KEY) !== null;
  const inSession = window.sessionStorage.getItem(KEY) !== null;
  if (!inLocal && !inSession) return;
  writeSession(session, inLocal);
}

/** What to call this person in the UI: Student, Super Admin, their staff role (e.g. Counsellor), or just Staff. */
export function roleLabel(session: Session): string {
  if (session.role === "student") return "Student";
  if (session.isSuperAdmin) return "Super Admin";
  return session.roleName || "Staff";
}

/** Heading above the sidebar: the Super Admin gets the Admin portal, everyone else on staff the Staff portal. */
export function portalLabel(session: Session): string {
  if (session.role === "student") return "Student Portal";
  return session.isSuperAdmin ? "Admin Portal" : "Staff Portal";
}

export function clearSession() {
  window.localStorage.removeItem(KEY);
  window.sessionStorage.removeItem(KEY);
}

const REMEMBER_KEY = "mva.remembered";

/** Last-used login identifier per role (never the password). */
export function readRemembered(): Partial<Record<Role, string>> {
  if (typeof window === "undefined") return {};
  try {
    const raw = JSON.parse(window.localStorage.getItem(REMEMBER_KEY) ?? "{}") as Record<string, unknown>;
    const out: Partial<Record<Role, string>> = {};
    for (const role of ["student", "employee"] as const) {
      const v = raw[role];
      if (typeof v === "string") out[role] = v;
      else if (v && typeof v === "object" && typeof (v as { identifier?: unknown }).identifier === "string")
        out[role] = (v as { identifier: string }).identifier;
    }
    return out;
  } catch {
    return {};
  }
}

const REMEMBER_PW_KEY = "mva.rememberedPw";

/** Last-used password per role, saved only when "Remember me" is ticked (stored as plain text in localStorage). */
export function readRememberedPasswords(): Partial<Record<Role, string>> {
  if (typeof window === "undefined") return {};
  try {
    const raw = JSON.parse(window.localStorage.getItem(REMEMBER_PW_KEY) ?? "{}") as Record<string, unknown>;
    const out: Partial<Record<Role, string>> = {};
    for (const role of ["student", "employee"] as const) {
      const v = raw[role];
      if (typeof v === "string") out[role] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function writeRemembered(role: Role, identifier: string | null, password: string | null = null) {
  const next = { ...readRemembered() };
  if (identifier) next[role] = identifier;
  else delete next[role];
  window.localStorage.setItem(REMEMBER_KEY, JSON.stringify(next));

  const nextPw = { ...readRememberedPasswords() };
  if (identifier && password) nextPw[role] = password;
  else delete nextPw[role];
  window.localStorage.setItem(REMEMBER_PW_KEY, JSON.stringify(nextPw));
}
